'use client';

/**
 * ToolTimeoutCard — the container that owns the tool-timeout recovery
 * flow's rendering — mirroring what HandoffTimeoutPanel does for the
 * handoff flow (same single-container-owns-the-one-live-API-call shape; see
 * that file's header comment). Purely a wiring layer: every visual state is
 * one of the already-built tool-timeout-*.tsx leaves.
 *
 * Pure presentational switch on `flow.state.view` — ALL of the view
 * machine, submitting/error state, retry-outcome detection, and submit
 * handlers live in useToolTimeoutFlow (src/hooks/), composed exactly ONCE
 * inside ChatProvider (contexts/ChatContext.tsx) and exposed as
 * `toolTimeoutFlow`. This component does NOT call that hook itself — it
 * used to (a per-mount instance, `key`-ed by tool name), but the transcript
 * this card lives in stays mounted UNDERNEATH the call overlay for the
 * whole duration of a call (page.tsx never gates this mount on call state),
 * so a second independent instance in voice/call-tool-timeout-panel.tsx
 * could silently diverge from this one on the SAME backend incident —
 * submitting on the call panel while this card kept showing OFFER, open to
 * a second submission once the call ended. See useToolTimeoutFlow's own
 * header comment for the full story. Both this component and
 * CallToolTimeoutPanel are now equally thin readers of the ONE shared
 * `flow` object, so they can never disagree.
 */
import { ToolTimeoutAction, ToolTimeoutView } from '@/lib/tool-timeout-view';
import { SupportRequestType } from '@/lib/api/support-requests';
import type { ToolTimeoutFlowContextValue } from '@/hooks/useToolTimeoutFlow';
import { ToolTimeoutOffer } from './tool-timeout-offer';
import { ToolTimeoutRetrying } from './tool-timeout-retrying';
import { ToolTimeoutFailed } from './tool-timeout-failed';
import { ToolTimeoutChoose } from './tool-timeout-choose';
import { ToolTimeoutMessage } from './tool-timeout-message';
import { ToolTimeoutCallback } from './tool-timeout-callback';
import { ToolTimeoutSubmitted } from './tool-timeout-submitted';
import { ToolTimeoutRecovered } from './tool-timeout-recovered';
import { ToolTimeoutChip } from './tool-timeout-chip';

interface ToolTimeoutCardProps {
  /** ChatContext.toolTimeoutFlow — the ONE shared instance (see this
   *  file's header comment). Callers gate this component's mount on it
   *  being non-null; nothing here re-derives that. */
  flow: ToolTimeoutFlowContextValue;
}

export function ToolTimeoutCard({ flow }: ToolTimeoutCardProps) {
  const {
    offer,
    phoneOnFile,
    state,
    dispatch,
    submitting,
    error,
    submittedEmail,
    submittedPhone,
    submittedWindow,
    handleRetry,
    handleResume,
    handleSubmitMessage,
    handleSubmitCallback,
  } = flow;
  const { retryAvailable } = offer;

  switch (state.view) {
    case ToolTimeoutView.OFFER:
      return (
        <ToolTimeoutOffer
          // FINDING B fix: a restored marker can arrive with attempt===1
          // AND retry_available===false (the backend's cap was already hit
          // in an earlier session) — ToolTimeoutFailed always passed this
          // through, OFFER never did, so that specific combination rendered
          // a "Try again" button the backend would reject on tap.
          retryAvailable={retryAvailable}
          onRetry={handleRetry}
          onLeaveDetails={() => dispatch(ToolTimeoutAction.LEAVE_DETAILS)}
          // FINDING 5: a failed SEND (socket not open) sets this via the
          // hook instead of entering RETRYING — see ToolTimeoutOffer's own
          // `error` doc comment.
          error={error}
        />
      );

    case ToolTimeoutView.RETRYING:
      return (
        <ToolTimeoutRetrying
          onSkip={() => dispatch(ToolTimeoutAction.LEAVE_DETAILS)}
        />
      );

    case ToolTimeoutView.RECOVERED:
      return <ToolTimeoutRecovered />;

    case ToolTimeoutView.FAILED:
      return (
        <ToolTimeoutFailed
          onLeaveDetails={() => dispatch(ToolTimeoutAction.LEAVE_DETAILS)}
          onRetry={handleRetry}
          retryAvailable={retryAvailable}
          error={error}
        />
      );

    case ToolTimeoutView.CHOOSE:
      return (
        <ToolTimeoutChoose
          onSelectMessage={() => dispatch(ToolTimeoutAction.CHOOSE_MESSAGE)}
          onSelectCallback={() => dispatch(ToolTimeoutAction.CHOOSE_CALLBACK)}
          onBack={() => dispatch(ToolTimeoutAction.BACK)}
        />
      );

    case ToolTimeoutView.MESSAGE:
      return (
        <ToolTimeoutMessage
          onBack={() => dispatch(ToolTimeoutAction.BACK)}
          onSubmit={handleSubmitMessage}
          submitting={submitting}
          error={error}
        />
      );

    case ToolTimeoutView.CALLBACK:
      return (
        <ToolTimeoutCallback
          phoneOnFile={phoneOnFile}
          onBack={() => dispatch(ToolTimeoutAction.BACK)}
          onSubmit={handleSubmitCallback}
          submitting={submitting}
          error={error}
        />
      );

    case ToolTimeoutView.SUBMITTED_MESSAGE:
      // SUBMITTED_MESSAGE only ever follows MESSAGE (see
      // nextToolTimeoutState's SUBMITTED case) — `state.view` is already the
      // authoritative record of which form this was; no separate
      // "submittedType" state needed to duplicate it.
      return (
        <ToolTimeoutSubmitted
          type={SupportRequestType.MESSAGE}
          email={submittedEmail}
        />
      );

    case ToolTimeoutView.SUBMITTED_CALLBACK:
      return (
        <ToolTimeoutSubmitted
          type={SupportRequestType.CALLBACK}
          phone={submittedPhone}
          callbackWindow={submittedWindow}
        />
      );

    case ToolTimeoutView.COLLAPSED:
      return <ToolTimeoutChip onRetry={handleResume} />;

    default:
      return null;
  }
}
